import type { ReactElement } from 'react';
import { Button, Heading, Select, Spinner, TagPicker, Text, TextField, type TagOption } from '@chatsdet/ui';
import { ArrowsClockwise, Pause, Play, Trash } from '@phosphor-icons/react';
import {
  SCHEDULE_PRESETS,
  formatNextRun,
  useDeleteScheduleRule,
  useRunScheduleNow,
  useToggleScheduleRule,
} from '../scheduling';
import { describeScheduleTarget } from './useSuiteSchedules';
import type { TestSuiteSchedule } from './types';
import { useEligibilityModel } from '../../hooks/useEligibilityModel';

export type SuiteScheduleTargetKey = 'all' | 'tag' | 'suite';

export interface SchedulePreview {
  cron: string;
  description: string;
}

const TARGET_OPTIONS = [
  { id: 'all' as const, label: 'All published workflows' },
  { id: 'tag' as const, label: 'Workflows by tag' },
  { id: 'suite' as const, label: 'A named suite' },
];

export function SuiteScheduleTargetPicker({
  target,
  onTargetChange,
  tags,
  selectedTags,
  onSelectedTagsChange,
  tagsLoading,
  suites,
  selectedSuiteId,
  onSelectedSuiteIdChange,
}: {
  target: SuiteScheduleTargetKey;
  onTargetChange: (target: SuiteScheduleTargetKey) => void;
  tags: TagOption[];
  selectedTags: TagOption[];
  onSelectedTagsChange: (tags: TagOption[]) => void;
  tagsLoading: boolean;
  suites: Array<{ id: string; name: string; kind: 'native' | 'external' }>;
  selectedSuiteId: string | null;
  onSelectedSuiteIdChange: (suiteId: string | null) => void;
}) {
  const runnableWord = useEligibilityModel() ? 'eligible' : 'promoted';
  return (
    <div className="space-y-4">
      <Select
        label="What to run"
        description={`Run every ${runnableWord} workflow, workflows carrying a tag, or a saved suite.`}
        items={TARGET_OPTIONS}
        selectedKey={target}
        onSelectionChange={(key) => onTargetChange(String(key) as SuiteScheduleTargetKey)}
      />
      {target === 'tag' && (
        <TagPicker
          label="Tags"
          placeholder="Add tags to run…"
          tags={tags}
          selectedTags={selectedTags}
          onChange={onSelectedTagsChange}
          isLoading={tagsLoading}
          description={`Runs all ${runnableWord} workflows carrying any of these tags.`}
        />
      )}
      {target === 'suite' && (
        <Select
          label="Suite"
          placeholder="Choose a suite…"
          items={suites.map((suite) => ({
            id: suite.id,
            label: suite.name,
            description: suite.kind === 'external' ? 'External test suite' : 'Workflow suite',
          }))}
          selectedKey={selectedSuiteId}
          onSelectionChange={(key) => onSelectedSuiteIdChange(key ? String(key) : null)}
        />
      )}
    </div>
  );
}

export function SchedulePresetButtons({
  tagMissing,
  isPending,
  helpText,
  onCreate,
}: {
  tagMissing: boolean;
  isPending: boolean;
  helpText: string;
  onCreate: (cron: string, description: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Heading level={3}>Quick schedules</Heading>
      <Text size="xs" className="text-muted-foreground text-pretty">
        {helpText}
      </Text>
      <div className="flex flex-wrap gap-2">
        {SCHEDULE_PRESETS.map((preset) => (
          <Button
            key={preset.label}
            variant="secondary"
            size="sm"
            isDisabled={tagMissing}
            isPending={isPending}
            onPress={() => onCreate(preset.cron, preset.description)}
          >
            {preset.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

export function ScheduleDescriptionComposer({
  text,
  preview,
  error,
  tagMissing,
  parsePending,
  createPending,
  submitLabel,
  onTextChange,
  onPreview,
  onCreate,
}: {
  text: string;
  preview: SchedulePreview | null;
  error: string | null;
  tagMissing: boolean;
  parsePending: boolean;
  createPending: boolean;
  submitLabel: string;
  onTextChange: (text: string) => void;
  onPreview: () => void;
  onCreate: (cron: string, description: string) => void;
}) {
  return (
    <div className="space-y-2">
      <Heading level={3}>Or describe one</Heading>
      <div className="flex items-end gap-2">
        <div className="flex-1">
          <TextField
            aria-label="Describe a schedule"
            placeholder="e.g. every weekday at 9am"
            value={text}
            onChange={onTextChange}
          />
        </div>
        <Button
          variant="secondary"
          onPress={onPreview}
          isPending={parsePending}
          isDisabled={!text.trim()}
        >
          Preview
        </Button>
      </div>

      {preview && (
        <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted px-3 py-2">
          <div className="min-w-0">
            <Text size="sm" weight="medium" className="truncate">
              {preview.description}
            </Text>
            <Text size="xs" className="text-muted-foreground font-mono">
              {preview.cron}
            </Text>
          </div>
          <Button
            variant="primary"
            size="sm"
            isDisabled={tagMissing}
            isPending={createPending}
            onPress={() => onCreate(preview.cron, preview.description)}
          >
            {submitLabel}
          </Button>
        </div>
      )}

      {error && (
        <Text size="sm" className="text-destructive">
          {error}
        </Text>
      )}
    </div>
  );
}

export function SuiteScheduleActiveList({
  schedules,
  isLoading,
  locked,
  renderEdit,
}: {
  schedules: TestSuiteSchedule[];
  isLoading: boolean;
  /** True when this modal is locked to one suite — otherwise these schedules span every target (all/tag/suite) in the org. */
  locked: boolean;
  /** The edit control for one schedule (opens the schedule form prefilled). A render prop because the modal imports this file, so this file cannot import the modal. */
  renderEdit: (schedule: TestSuiteSchedule) => ReactElement;
}) {
  return (
    <div className="space-y-4">
      <div>
        <Heading level={2}>Active schedules</Heading>
        <Text size="xs" className="mt-0.5 text-muted-foreground text-pretty">
          {locked
            ? 'Every recurring run configured for this suite.'
            : 'Every recurring run configured for this org.'}
        </Text>
      </div>
      {isLoading ? (
        <div className="py-3">
          <Spinner size="sm" />
        </div>
      ) : schedules.length === 0 ? (
        <div className="rounded-lg border border-border bg-background px-3 py-2.5">
          <Text size="sm" className="text-muted-foreground">
            No schedules yet.
          </Text>
        </div>
      ) : (
        <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-background">
          {schedules.map((schedule) => (
            <SuiteScheduleRow
              key={schedule.ruleId}
              schedule={schedule}
              editControl={renderEdit(schedule)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Last path segment, for a short area name in the schedule row's summary. */
function areaShortLabel(path: string): string {
  const segments = path.split('/').filter(Boolean);
  return segments[segments.length - 1] ?? path;
}

function SuiteScheduleRow({
  schedule,
  editControl,
}: {
  schedule: TestSuiteSchedule;
  editControl: ReactElement;
}) {
  const toggleMutation = useToggleScheduleRule();
  const runNowMutation = useRunScheduleNow();
  const deleteMutation = useDeleteScheduleRule();

  return (
    <div className="px-3 py-2.5">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Text size="sm" weight="medium" className="truncate">
            {schedule.description}
          </Text>
          <Text size="xs" className="text-muted-foreground truncate">
            {describeScheduleTarget(schedule)}
            {schedule.environmentId ? ` · ${schedule.environmentName ?? 'Environment not found'}` : ''} ·{' '}
            {schedule.enabled ? `Next run ${formatNextRun(schedule.nextRunAt)}` : 'Paused'}
            {schedule.externalAreas.length > 0
              ? ` · Area${schedule.externalAreas.length === 1 ? '' : 's'}: ${schedule.externalAreas.map(areaShortLabel).join(', ')}`
              : ''}
            {schedule.alertConfig.steps.length > 0
              ? ` · ${schedule.alertConfig.steps.length} alert${schedule.alertConfig.steps.length === 1 ? '' : 's'}`
              : ''}
          </Text>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {editControl}
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip={schedule.enabled ? 'Pause' : 'Resume'}
            iconLeft={schedule.enabled ? <Pause size={15} /> : <Play size={15} />}
            onPress={() => toggleMutation.mutate(schedule.ruleId)}
            isPending={toggleMutation.isPending}
          />
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tooltip="Run now"
            iconLeft={<ArrowsClockwise size={15} />}
            onPress={() => runNowMutation.mutate(schedule.ruleId)}
            isPending={runNowMutation.isPending}
          />
          <Button
            variant="icon"
            size="sm"
            iconOnly
            tone="destructive"
            tooltip="Delete schedule"
            iconLeft={<Trash size={15} />}
            onPress={() => deleteMutation.mutate(schedule.ruleId)}
            isPending={deleteMutation.isPending}
          />
        </div>
      </div>
    </div>
  );
}
